import React, { useState, useEffect, useRef } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import toast from 'react-hot-toast'
import {
  ArrowPathIcon,
  CloudIcon,
  FolderIcon,
  DocumentIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  XMarkIcon,
  PhotoIcon,
  EyeIcon,
  FilmIcon,
  MusicalNoteIcon,
} from '@heroicons/react/24/outline'

function fileDisplayName(file) {
  return file.name.split('/').pop() || file.name
}

function parseStorageFilePath(file) {
  const pathParts = file.name.split('/')
  if (pathParts.length < 3) return null
  const [category, year, ...filenameParts] = pathParts
  const filename = filenameParts.join('/')
  return { category, year, filename, displayName: fileDisplayName(file) }
}

function previewKindFromFile(file) {
  const mime = (file.mimeType || '').toLowerCase()
  const name = fileDisplayName(file)
  const ext = (name.split('.').pop() || '').toLowerCase()
  if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return 'image'
  }
  if (mime.includes('pdf') || ext === 'pdf') return 'pdf'
  if (
    mime.startsWith('text/') ||
    ['txt', 'csv', 'md', 'json', 'xml', 'html', 'htm', 'log'].includes(ext)
  ) {
    return 'text'
  }
  if (mime.startsWith('video/') || ['mp4', 'webm', 'mov'].includes(ext)) return 'video'
  if (mime.startsWith('audio/') || ['mp3', 'wav', 'ogg'].includes(ext)) return 'audio'
  return 'other'
}

function isPreviewable(file) {
  const k = previewKindFromFile(file)
  return k !== 'other'
}

function FileTypeThumb({ file }) {
  const kind = previewKindFromFile(file)
  const base =
    'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50'
  if (kind === 'image') {
    return (
      <div className={`${base} border-cyan-200 bg-cyan-50`} title="Image">
        <PhotoIcon className="h-6 w-6 text-cyan-700" />
      </div>
    )
  }
  if (kind === 'pdf') {
    return (
      <div className={`${base} border-red-200 bg-red-50`} title="PDF">
        <span className="text-[10px] font-bold text-red-700">PDF</span>
      </div>
    )
  }
  if (kind === 'text') {
    return (
      <div className={`${base} border-amber-200 bg-amber-50`} title="Text">
        <DocumentTextIcon className="h-6 w-6 text-amber-800" />
      </div>
    )
  }
  if (kind === 'video') {
    return (
      <div className={`${base} border-violet-200 bg-violet-50`} title="Video">
        <FilmIcon className="h-6 w-6 text-violet-700" />
      </div>
    )
  }
  if (kind === 'audio') {
    return (
      <div className={`${base} border-indigo-200 bg-indigo-50`} title="Audio">
        <MusicalNoteIcon className="h-6 w-6 text-indigo-700" />
      </div>
    )
  }
  return (
    <div className={base} title="File">
      <DocumentIcon className="h-6 w-6 text-gray-500" />
    </div>
  )
}

export default function StorageMonitoring({ fixedGroupId = '', embedded = false }) {
  const [stats, setStats] = useState(null)
  const [files, setFiles] = useState([])
  const [recentFiles, setRecentFiles] = useState([])
  const [health, setHealth] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Tenant selection
  const [tenants, setTenants] = useState([])
  const [selectedTenant, setSelectedTenant] = useState(
    embedded && fixedGroupId ? fixedGroupId : 'platform'
  )
  const [tenantsLoading, setTenantsLoading] = useState(!embedded)

  useEffect(() => {
    if (fixedGroupId) setSelectedTenant(fixedGroupId)
  }, [fixedGroupId])
  
  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadCategory, setUploadCategory] = useState('archive')
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)
  
  // Folder creation modal
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [initializingFolders, setInitializingFolders] = useState(false)
  const [customFolderName, setCustomFolderName] = useState('')
  const [customFoldersList, setCustomFoldersList] = useState([])

  const [preview, setPreview] = useState(null)

  // Filters
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState('desc')
  const [limit, setLimit] = useState(50)

  useEffect(() => {
    if (!embedded) loadTenants()
  }, [embedded])

  useEffect(() => {
    if (embedded || !tenantsLoading) {
      loadData()
      const interval = setInterval(loadData, 60000) // Refresh every minute
      return () => clearInterval(interval)
    }
  }, [selectedTenant, tenantsLoading, embedded])

  useEffect(() => {
    if (categories.length > 0) {
      loadFiles()
    }
  }, [selectedCategory, selectedYear, sortBy, sortOrder, limit])

  const loadTenants = async () => {
    if (embedded) return
    try {
      setTenantsLoading(true)
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants'))
      const data = await res.json()
      if (data.success) {
        setTenants(data.tenants || [])
      }
    } catch (err) {
      console.error('Failed to load tenants:', err)
    } finally {
      setTenantsLoading(false)
    }
  }

  const loadData = async () => {
    try {
      setLoading(true)

      const tenantParam = selectedTenant !== 'platform' ? `?tenantId=${selectedTenant}` : ''

      const [statsRes, healthRes, categoriesRes, recentRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/admin/storage/stats${tenantParam}`)),
        authFetch(getLocalApiUrl(`/api/admin/storage/health${tenantParam}`)),
        authFetch(getLocalApiUrl('/api/admin/storage/categories')),
        authFetch(getLocalApiUrl(`/api/admin/storage/recent?limit=10&hours=24${selectedTenant !== 'platform' ? `&tenantId=${selectedTenant}` : ''}`))
      ])

      const [statsData, healthData, categoriesData, recentData] = await Promise.all([
        statsRes.json(),
        healthRes.json(),
        categoriesRes.json(),
        recentRes.json()
      ])

      if (statsData.success) setStats(statsData.data)
      if (healthData.success) setHealth(healthData.data)
      if (categoriesData.success) setCategories(categoriesData.data.categories)
      if (recentData.success) setRecentFiles(recentData.data.files)

      await loadFiles()
    } catch (err) {
      console.error('Failed to load storage data:', err)
      toast.error('Failed to load storage data')
    } finally {
      setLoading(false)
    }
  }

  const loadFiles = async () => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        sortBy,
        sortOrder
      })
      if (selectedCategory) params.append('category', selectedCategory)
      if (selectedYear) params.append('year', selectedYear)
      if (selectedTenant !== 'platform') params.append('tenantId', selectedTenant)

      const res = await authFetch(getLocalApiUrl(`/api/admin/storage/files?${params}`))
      const data = await res.json()
      if (data.success) {
        setFiles(data.data.files)
      }
    } catch (err) {
      console.error('Failed to load files:', err)
    }
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return '—'
    const d = new Date(dateString)
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getCategoryColor = (category) => {
    const colors = {
      'kyc-documents': 'bg-blue-100 text-blue-800',
      'kyc-photos': 'bg-cyan-100 text-cyan-800',
      'signatures': 'bg-green-100 text-green-800',
      'incidents': 'bg-yellow-100 text-yellow-800',
      'smr': 'bg-red-100 text-red-800',
      'investigations': 'bg-purple-100 text-purple-800',
      'obligations': 'bg-gray-100 text-gray-800',
      'archive': 'bg-slate-100 text-slate-800'
    }
    return colors[category] || 'bg-gray-100 text-gray-800'
  }

  const handleFileSelect = (e) => {
    if (e.target.files) {
      setUploadFiles(Array.from(e.target.files))
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setUploadFiles(Array.from(e.dataTransfer.files))
    }
  }

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select files to upload')
      return
    }

    setUploading(true)
    let successCount = 0
    let errorCount = 0

    for (const file of uploadFiles) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('category', uploadCategory)
        formData.append('tenantId', selectedTenant)

        const res = await authFetch(getLocalApiUrl('/api/admin/storage/upload'), {
          method: 'POST',
          body: formData,
        })

        const data = await res.json()
        
        if (data.success) {
          successCount++
        } else {
          errorCount++
          console.error(`Upload failed for ${file.name}:`, data.error)
        }
      } catch (err) {
        errorCount++
        console.error(`Upload failed for ${file.name}:`, err)
      }
    }

    setUploading(false)
    
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file(s)`)
      setShowUploadModal(false)
      setUploadFiles([])
      loadData() // Refresh data
    }
    
    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} file(s)`)
    }
  }

  const handleDownload = async (file) => {
    try {
      // Parse the file path to get category, year, and filename
      const pathParts = file.name.split('/')
      if (pathParts.length < 3) {
        toast.error('Invalid file path')
        return
      }

      const [category, year, ...filenameParts] = pathParts
      const filename = filenameParts.join('/')

      const tenantParam = selectedTenant !== 'platform' ? `?tenantId=${selectedTenant}` : ''
      const res = await authFetch(
        getLocalApiUrl(`/api/admin/storage/download/${category}/${year}/${filename}${tenantParam}`)
      )

      if (!res.ok) {
        throw new Error('Download failed')
      }

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success('File downloaded successfully')
    } catch (err) {
      console.error('Download failed:', err)
      toast.error('Failed to download file')
    }
  }

  const handleDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete "${file.name.split('/').pop()}"?`)) {
      return
    }

    try {
      // Parse the file path
      const pathParts = file.name.split('/')
      if (pathParts.length < 3) {
        toast.error('Invalid file path')
        return
      }

      const [category, year, ...filenameParts] = pathParts
      const filename = filenameParts.join('/')

      const tenantParam = selectedTenant !== 'platform' ? `?tenantId=${selectedTenant}` : ''
      const res = await authFetch(
        getLocalApiUrl(`/api/admin/storage/delete/${category}/${year}/${filename}${tenantParam}`),
        { method: 'DELETE' }
      )

      const data = await res.json()

      if (data.success) {
        toast.success('File deleted successfully')
        loadData() // Refresh data
      } else {
        toast.error(data.error || 'Failed to delete file')
      }
    } catch (err) {
      console.error('Delete failed:', err)
      toast.error('Failed to delete file')
    }
  }

  const handleInitializeFolders = async () => {
    if (!window.confirm('Initialize folder structure? This will create placeholder files in selected categories.')) {
      return
    }

    setInitializingFolders(true)

    try {
      const body = {
        tenantId: selectedTenant,
        skipExisting: true
      }

      // If custom folders specified, send them
      if (customFoldersList.length > 0) {
        body.customFolders = customFoldersList
      }

      const res = await authFetch(getLocalApiUrl('/api/admin/storage/initialize'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      const data = await res.json()

      if (data.success) {
        toast.success(data.message || 'Folders initialized successfully')
        setShowFolderModal(false)
        setCustomFoldersList([])
        setCustomFolderName('')
        loadData() // Refresh data
      } else {
        toast.error(data.error || 'Failed to initialize folders')
      }
    } catch (err) {
      console.error('Folder initialization failed:', err)
      toast.error('Failed to initialize folders')
    } finally {
      setInitializingFolders(false)
    }
  }

  const handleAddCustomFolder = () => {
    const folderName = customFolderName.trim()
    if (!folderName) return

    // Validate folder name
    if (!/^[a-zA-Z0-9_-]+$/.test(folderName)) {
      toast.error('Folder name must contain only letters, numbers, hyphens, and underscores')
      return
    }

    if (customFoldersList.includes(folderName)) {
      toast.error('Folder already in list')
      return
    }

    setCustomFoldersList([...customFoldersList, folderName])
    setCustomFolderName('')
  }

  const handleRemoveCustomFolder = (folderName) => {
    setCustomFoldersList(customFoldersList.filter(f => f !== folderName))
  }

  const closePreview = () => {
    if (preview?.url) {
      try {
        URL.revokeObjectURL(preview.url)
      } catch {
        /* ignore */
      }
    }
    setPreview(null)
  }

  const openPreview = async (file) => {
    if (!isPreviewable(file)) {
      toast.error('No in-browser preview for this file type — use Download.')
      return
    }
    const p = parseStorageFilePath(file)
    if (!p) {
      toast.error('Invalid file path')
      return
    }
    const qs = new URLSearchParams()
    qs.set('inline', '1')
    if (selectedTenant !== 'platform') qs.set('tenantId', selectedTenant)
    const url = getLocalApiUrl(
      `/api/admin/storage/download/${encodeURIComponent(p.category)}/${encodeURIComponent(p.year)}/${encodeURIComponent(p.filename)}?${qs.toString()}`
    )
    try {
      const res = await authFetch(url)
      if (!res.ok) {
        toast.error('Could not load file')
        return
      }
      const blob = await res.blob()
      const mime = res.headers.get('content-type') || file.mimeType || ''
      const kind = previewKindFromFile({ ...file, mimeType: mime })

      if (kind === 'text') {
        const text = await blob.text()
        setPreview({ kind: 'text', text, name: p.displayName })
        return
      }

      const blobUrl = URL.createObjectURL(blob)
      setPreview({ kind, url: blobUrl, name: p.displayName, mime })
    } catch (e) {
      console.error(e)
      toast.error('Preview failed')
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-psg-teal mx-auto mb-3" />
          <p className="text-gray-600">Loading storage data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {!embedded ? (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CloudIcon className="h-7 w-7 text-psg-teal" />
              Azure Blob Storage
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Monitor platform and tenant blob storage — use the selector below or open a tenant workspace tab
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            Blob storage for <span className="font-mono font-semibold text-teal-800">{fixedGroupId}</span>.
            Platform-wide storage is under <strong>Storage</strong> in the sidebar.
          </p>
        )}
        <div className="flex items-center gap-3 ml-auto">
          <button
            onClick={() => setShowFolderModal(true)}
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <FolderIcon className="h-4 w-4" />
            Initialize Folders
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 rounded-md bg-psg-teal px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-psg-teal/90"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            Upload Files
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tenant Selector */}
      <div className="bg-white shadow-sm rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          {embedded ? (
            <span className="text-sm text-gray-700">
              <span className="font-medium">Storage context:</span>{' '}
              <span className="font-mono text-teal-800">{fixedGroupId}</span>
            </span>
          ) : (
            <>
              <label className="text-sm font-medium text-gray-700">Storage context:</label>
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                disabled={tenantsLoading}
                className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal sm:text-sm"
              >
                <option value="platform">Platform (global)</option>
                {tenants.map((tenant) => (
                  <option key={tenant.group_id} value={tenant.group_id}>
                    {tenant.name || tenant.group_id} ({tenant.group_id})
                  </option>
                ))}
              </select>
            </>
          )}
          {stats && (
            <span className="text-xs text-gray-500">
              Viewing: <span className="font-mono font-medium">{stats.container || stats.localPath}</span>
            </span>
          )}
        </div>
      </div>

      {/* Health Status */}
      {health && (
        <div className={`rounded-lg p-4 ${health.accessible ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {health.accessible ? (
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              ) : (
                <XCircleIcon className="h-6 w-6 text-yellow-600" />
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">
                  <span className="font-semibold">{health.storageType.toUpperCase()}</span>
                  {health.configured && ` · Container: ${health.container}`}
                  {!health.configured && health.localPath && ` · ${health.localPath}`}
                </p>
                {health.error && <p className="text-xs text-red-600 mt-1">{health.error}</p>}
              </div>
            </div>
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${health.accessible ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {health.accessible ? 'Connected' : 'Connection Failed'}
            </span>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">Total Files</dt>
                    <dd className="text-2xl font-semibold text-gray-900">{stats.totalFiles.toLocaleString()}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CloudIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">Total Storage</dt>
                    <dd className="text-2xl font-semibold text-gray-900">{stats.totalSizeMB.toFixed(1)} MB</dd>
                    <dd className="text-xs text-gray-500">{formatBytes(stats.totalSize)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FolderIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">Categories</dt>
                    <dd className="text-2xl font-semibold text-gray-900">{stats.categories.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {stats && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Storage by Category</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Files</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size (MB)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Modified</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.categories.map((cat) => (
                  <tr key={cat.category} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getCategoryColor(cat.category)}`}>
                        {cat.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{cat.fileCount.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{cat.totalSizeMB.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(cat.lastModified)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Uploads */}
      {recentFiles.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-gray-400" />
              Recent Uploads (Last 24 Hours)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentFiles.map((file, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 align-middle">
                      <FileTypeThumb file={file} />
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-900 truncate max-w-xs">{file.name.split('/').pop()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getCategoryColor(file.category)}`}>
                        {file.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{file.sizeMB.toFixed(2)} MB</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(file.lastModified)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {isPreviewable(file) ? (
                        <button
                          type="button"
                          onClick={() => openPreview(file)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-psg-teal hover:bg-psg-teal/10"
                          title="Preview"
                        >
                          <EyeIcon className="h-4 w-4" />
                          Preview
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* File Browser */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">File Browser</h3>
        </div>
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal text-sm"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal text-sm"
              >
                <option value="">All Years</option>
                {[2026, 2025, 2024].map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal text-sm"
              >
                <option value="date">Date</option>
                <option value="name">Name</option>
                <option value="size">Size</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal text-sm"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Limit</label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal text-sm"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size (MB)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Modified</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {files.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                    No files found
                  </td>
                </tr>
              )}
              {files.map((file, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 align-middle">
                    <FileTypeThumb file={file} />
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-gray-900 truncate max-w-md">{file.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getCategoryColor(file.category)}`}>
                      {file.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{file.sizeMB.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(file.lastModified)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {isPreviewable(file) && (
                        <button
                          type="button"
                          onClick={() => openPreview(file)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-psg-teal hover:bg-psg-teal/10"
                          title="Preview"
                        >
                          <EyeIcon className="h-4 w-4" />
                          <span className="hidden sm:inline">Preview</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(file)}
                        className="inline-flex items-center gap-1 text-psg-teal hover:text-psg-teal/80"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(file)}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !uploading && setShowUploadModal(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => !uploading && setShowUploadModal(false)}
                  disabled={uploading}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-psg-teal/10 sm:mx-0 sm:h-10 sm:w-10">
                  <ArrowUpTrayIcon className="h-6 w-6 text-psg-teal" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Upload Files</h3>
                  <p className="mt-1 text-sm text-gray-500">Upload files to Azure Blob Storage</p>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                    <span className="ml-2 text-xs text-gray-500">(or enter custom folder name)</span>
                  </label>
                  <input
                    type="text"
                    list="category-list"
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    disabled={uploading}
                    placeholder="Select or type category name"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal"
                  />
                  <datalist id="category-list">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                  <p className="mt-1 text-xs text-gray-500">
                    Choose from standard categories or type a custom folder name
                  </p>
                </div>

                {/* File Drop Zone */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center ${
                    dragActive ? 'border-psg-teal bg-psg-teal/5' : 'border-gray-300'
                  } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                >
                  <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">
                    {uploadFiles.length > 0
                      ? `${uploadFiles.length} file(s) selected`
                      : 'Drag & drop files here, or click to select'}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    disabled={uploading}
                    className="hidden"
                  />
                </div>

                {/* Selected Files List */}
                {uploadFiles.length > 0 && (
                  <div className="max-h-40 overflow-y-auto">
                    <ul className="divide-y divide-gray-200 text-sm">
                      {uploadFiles.map((file, idx) => (
                        <li key={idx} className="py-2 flex items-center justify-between">
                          <span className="text-gray-700 truncate">{file.name}</span>
                          <span className="text-gray-500 ml-2">{formatBytes(file.size)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading || uploadFiles.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-psg-teal text-base font-medium text-white hover:bg-psg-teal/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-psg-teal sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5" />
                      Uploading...
                    </>
                  ) : (
                    <>Upload {uploadFiles.length > 0 && `(${uploadFiles.length})`}</>
                  )}
                </button>
                <button
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-psg-teal sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Folder Initialization Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !initializingFolders && setShowFolderModal(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => !initializingFolders && setShowFolderModal(false)}
                  disabled={initializingFolders}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <FolderIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Initialize Folder Structure</h3>
                  <p className="mt-1 text-sm text-gray-500">Create standard category folders in Azure Blob Storage</p>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {/* Custom Folder Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Folder (Optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customFolderName}
                      onChange={(e) => setCustomFolderName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCustomFolder()}
                      placeholder="e.g., reports, documents"
                      disabled={initializingFolders}
                      className="flex-1 block rounded-md border-gray-300 shadow-sm focus:border-psg-teal focus:ring-psg-teal sm:text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomFolder}
                      disabled={initializingFolders || !customFolderName.trim()}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-psg-teal hover:bg-psg-teal/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Letters, numbers, hyphens, and underscores only
                  </p>
                </div>

                {/* Custom Folders List */}
                {customFoldersList.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Folders to Create ({customFoldersList.length})
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {customFoldersList.map((folder) => (
                        <span
                          key={folder}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          <span className="font-mono">{folder}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomFolder(folder)}
                            disabled={initializingFolders}
                            className="inline-flex items-center hover:text-blue-900 disabled:opacity-50"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Standard Folders Info */}
                <div className="rounded-md bg-blue-50 p-4">
                  <div className="text-sm text-blue-700">
                    {customFoldersList.length > 0 ? (
                      <>
                        <p className="font-medium mb-2">Custom folders will be created</p>
                        <p className="text-xs">Leave empty to create all standard folders instead</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium mb-2">Standard folders will be created:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs ml-2">
                          <li><span className="font-mono">smr</span> - Suspicious Matter Reports</li>
                          <li><span className="font-mono">investigations</span> - Investigation documents</li>
                          <li><span className="font-mono">obligations</span> - Compliance obligations</li>
                          <li><span className="font-mono">kyc-documents</span> - ID cards, licenses, cheques</li>
                          <li><span className="font-mono">kyc-photos</span> - Patron selfies</li>
                          <li><span className="font-mono">signatures</span> - Payment signatures</li>
                          <li><span className="font-mono">incidents</span> - Incident photos</li>
                          <li><span className="font-mono">archive</span> - Archived files</li>
                        </ul>
                      </>
                    )}
                    <p className="mt-3 text-xs font-medium">Existing folders will be skipped (safe to run multiple times).</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  onClick={handleInitializeFolders}
                  disabled={initializingFolders}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {initializingFolders ? (
                    <>
                      <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5" />
                      Initializing...
                    </>
                  ) : (
                    'Initialize Folders'
                  )}
                </button>
                <button
                  onClick={() => setShowFolderModal(false)}
                  disabled={initializingFolders}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close preview"
            onClick={closePreview}
          />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="truncate pr-4 text-sm font-semibold text-gray-900">{preview.name}</h3>
              <button
                type="button"
                onClick={closePreview}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto bg-gray-100 p-4">
              {preview.kind === 'image' && preview.url && (
                <img src={preview.url} alt={preview.name} className="mx-auto max-h-[75vh] max-w-full object-contain" />
              )}
              {preview.kind === 'pdf' && preview.url && (
                <iframe
                  title={preview.name}
                  src={preview.url}
                  className="h-[75vh] w-full rounded border border-gray-200 bg-white"
                />
              )}
              {preview.kind === 'video' && preview.url && (
                <video src={preview.url} controls className="mx-auto max-h-[75vh] max-w-full" playsInline />
              )}
              {preview.kind === 'audio' && preview.url && (
                <div className="flex justify-center py-8">
                  <audio src={preview.url} controls className="w-full max-w-md" />
                </div>
              )}
              {preview.kind === 'text' && preview.text != null && (
                <pre className="max-h-[75vh] overflow-auto rounded border border-gray-200 bg-white p-4 text-xs text-gray-800 whitespace-pre-wrap break-words font-mono">
                  {preview.text}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
